/**
 * React hooks for attendance tracking
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as attendanceService from '../services/attendance';
import { rsvpKeys } from './useRsvps';
import type { EventAttendanceWithProfile } from '../types/attendance.types';

export const attendanceKeys = {
  all: ['attendance'] as const,
  lists: () => [...attendanceKeys.all, 'list'] as const,
  byEvent: (eventId: number) =>
    [...attendanceKeys.lists(), 'event', eventId] as const,
};

export function useEventAttendance(eventId: number) {
  return useQuery<EventAttendanceWithProfile[]>({
    queryKey: attendanceKeys.byEvent(eventId),
    queryFn: () => attendanceService.getEventAttendance(eventId),
    enabled: !!eventId,
  });
}

export function useSyncEventAttendance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      eventId,
      attendeeIds,
    }: {
      eventId: number;
      attendeeIds: string[];
    }) => attendanceService.syncEventAttendance({ eventId, attendeeIds }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: attendanceKeys.byEvent(variables.eventId),
      });
      queryClient.invalidateQueries({
        queryKey: rsvpKeys.byEvent(variables.eventId),
      });
    },
  });
}

export function useDeleteAttendance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      attendanceId,
    }: {
      attendanceId: number;
      eventId: number;
    }) => attendanceService.deleteAttendance(attendanceId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: attendanceKeys.byEvent(variables.eventId),
      });
      queryClient.invalidateQueries({
        queryKey: rsvpKeys.byEvent(variables.eventId),
      });
    },
  });
}


